import React, { useState, useEffect, useCallback } from "react";
import {
  Switch,
  Card,
  Typography,
  Space,
  message,
  Spin,
  Alert,
  Divider,
  Button,
} from "antd";
import { supabase } from "../lib/supabase";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

interface EmailPreference {
  status: string;
  enabled: boolean;
  notification_type?: string;
}

interface FieldPreference {
  notification_type: string;
  enabled: boolean;
}

// Statuses that are always handled by n8n (cannot be disabled)
const n8nHandledStatuses = ["submitted", "approved", "rejected"];

// Statuses that are excluded from email notifications entirely
const excludedStatuses = ["concept", "archived"];

export const UserEmailPreferences: React.FC = () => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState<EmailPreference[]>([]);
  const [fieldPreferences, setFieldPreferences] = useState<FieldPreference[]>(
    []
  );
  const [globalEnabled, setGlobalEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [updatingGlobal, setUpdatingGlobal] = useState(false);

  const fetchPreferences = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("user_email_notification_preferences")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;

      // Get global preference
      const globalPref = data?.find(
        (p) => p.notification_type === "global" && p.status === "global"
      );
      setGlobalEnabled(globalPref?.global_enabled ?? false); // Default to disabled (opt-in)

      // expected_delivery_date_change: bookings.eta or containers.eta_customer
      // container_operational_eta_change: containers.eta
      // container_etd_change: COALESCE(containers.delayed_etd, containers.etd)
      const fieldTypes = [
        {
          type: "expected_delivery_date_change",
          defaultEnabled: false,
        },
        {
          type: "container_operational_eta_change",
          defaultEnabled: false,
        },
        {
          type: "container_etd_change",
          defaultEnabled: false,
        },
      ];
      const fieldPrefs = fieldTypes.map(({ type, defaultEnabled }) => {
        const existing = data?.find(
          (p) => p.notification_type === type && p.status === type
        );
        return {
          notification_type: type,
          enabled: existing?.enabled ?? defaultEnabled,
        };
      });
      setFieldPreferences(fieldPrefs);

      // Create preferences for all statuses, excluding concept and archived
      const allStatuses = [
        "waiting_for_goods",
        "goods_received_origin",
        "in_transit",
        "customs_pending_clearance",
        "customs_cleared",
        "fyco",
        "delivery_planned",
        "out_for_delivery",
        "delivered",
        "added_to_stock",
        "cancelled",
      ];
      // Statuses that are enabled by default (but can be disabled)
      const defaultEnabledStatuses = ["fyco", "delivery_planned"];

      const userPreferences = allStatuses.map((status) => {
        const existing = data?.find(
          (p) =>
            p.status === status &&
            (p.notification_type === "status_change" || !p.notification_type)
        );
        return {
          status,
          // For 'fyco' and 'delivery_planned': enabled by default (NULL = enabled), unless explicitly disabled
          // For other statuses: default to disabled (opt-in)
          enabled: defaultEnabledStatuses.includes(status)
            ? existing?.enabled ?? true // NULL or true = enabled, false = disabled
            : existing?.enabled ?? false,
        };
      });

      setPreferences(userPreferences);
    } catch (error) {
      console.error("Error fetching preferences:", error);
      message.error(t("emailPreferences.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const updateGlobalPreference = async (enabled: boolean) => {
    setUpdatingGlobal(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("user_email_notification_preferences")
        .upsert(
          {
            user_id: user.id,
            status: "global",
            notification_type: "global",
            global_enabled: enabled,
            enabled: true, // This field is not used for global preference
          },
          {
            onConflict: "user_id,status,notification_type",
          }
        );

      if (error) throw error;

      setGlobalEnabled(enabled);
      message.success(t("emailPreferences.updateSuccess"));
    } catch (error) {
      console.error("Error updating global preference:", error);
      message.error(t("emailPreferences.updateError"));
    } finally {
      setUpdatingGlobal(false);
    }
  };

  const updateFieldPreference = async (
    notificationType: string,
    enabled: boolean
  ) => {
    setUpdating(notificationType);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("user_email_notification_preferences")
        .upsert(
          {
            user_id: user.id,
            status: notificationType,
            notification_type: notificationType,
            enabled,
          },
          {
            onConflict: "user_id,status,notification_type",
          }
        );

      if (error) throw error;

      setFieldPreferences((prev) =>
        prev.map((p) =>
          p.notification_type === notificationType ? { ...p, enabled } : p
        )
      );

      message.success(t("emailPreferences.updateSuccess"));
    } catch (error) {
      console.error("Error updating field preference:", error);
      message.error(t("emailPreferences.updateError"));
    } finally {
      setUpdating(null);
    }
  };

  const updatePreference = async (status: string, enabled: boolean) => {
    setUpdating(status);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("user_email_notification_preferences")
        .upsert(
          {
            user_id: user.id,
            status,
            notification_type: "status_change",
            enabled,
          },
          {
            onConflict: "user_id,status,notification_type",
          }
        );

      if (error) throw error;

      setPreferences((prev) =>
        prev.map((p) => (p.status === status ? { ...p, enabled } : p))
      );

      message.success(t("emailPreferences.updateSuccess"));
    } catch (error) {
      console.error("Error updating preference:", error);
      message.error(t("emailPreferences.updateError"));
    } finally {
      setUpdating(null);
    }
  };

  const updateAllFieldPreferences = async (enabled: boolean) => {
    if (!globalEnabled) {
      message.warning(t("emailPreferences.enableGlobalFirst"));
      return;
    }

    setUpdating("all_fields");
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const updates = fieldPreferences.map((pref) =>
        supabase.from("user_email_notification_preferences").upsert(
          {
            user_id: user.id,
            status: pref.notification_type,
            notification_type: pref.notification_type,
            enabled,
          },
          {
            onConflict: "user_id,status,notification_type",
          }
        )
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) throw errors[0].error;

      setFieldPreferences((prev) => prev.map((p) => ({ ...p, enabled })));

      message.success(
        enabled
          ? t("emailPreferences.allFieldUpdatesEnabled")
          : t("emailPreferences.allFieldUpdatesDisabled")
      );
    } catch (error) {
      console.error("Error updating all field preferences:", error);
      message.error(t("emailPreferences.updateError"));
    } finally {
      setUpdating(null);
    }
  };

  const updateAllStatusPreferences = async (enabled: boolean) => {
    if (!globalEnabled) {
      message.warning(t("emailPreferences.enableGlobalFirst"));
      return;
    }

    setUpdating("all_statuses");
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      // Filter out n8n-handled statuses (but allow default-enabled statuses to be toggled)
      const updatableStatuses = preferences.filter(
        (p) => !n8nHandledStatuses.includes(p.status)
      );

      const updates = updatableStatuses.map((pref) =>
        supabase.from("user_email_notification_preferences").upsert(
          {
            user_id: user.id,
            status: pref.status,
            notification_type: "status_change",
            enabled,
          },
          {
            onConflict: "user_id,status,notification_type",
          }
        )
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) throw errors[0].error;

      setPreferences((prev) =>
        prev.map((p) =>
          n8nHandledStatuses.includes(p.status) ? p : { ...p, enabled }
        )
      );

      message.success(
        enabled
          ? t("emailPreferences.allStatusUpdatesEnabled")
          : t("emailPreferences.allStatusUpdatesDisabled")
      );
    } catch (error) {
      console.error("Error updating all status preferences:", error);
      message.error(t("emailPreferences.updateError"));
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <Card title={t("emailPreferences.title")}>
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px" }}>
            {t("emailPreferences.loading")}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card title={t("emailPreferences.title")}>
      <Alert
        message={t("emailPreferences.title")}
        description={t("emailPreferences.description")}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" style={{ width: "100%" }}>
        {/* Global Toggle */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 0",
            borderBottom: "2px solid #d9d9d9",
            marginBottom: "8px",
          }}
        >
          <div>
            <Text strong style={{ fontSize: "16px" }}>
              {t("emailPreferences.globalToggleTitle")}
            </Text>
            <div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {t("emailPreferences.globalToggleDescription")}
              </Text>
            </div>
          </div>
          <Switch
            checked={globalEnabled}
            onChange={updateGlobalPreference}
            disabled={updatingGlobal}
            loading={updatingGlobal}
          />
        </div>

        <Divider orientation="left" style={{ margin: "16px 0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong>{t("emailPreferences.fieldUpdates")}</Text>
            {globalEnabled && fieldPreferences.length > 0 && (
              <Space size="small">
                <Button
                  size="small"
                  type="link"
                  onClick={() => updateAllFieldPreferences(true)}
                  disabled={updating === "all_fields"}
                  loading={updating === "all_fields"}
                >
                  {t("emailPreferences.selectAll")}
                </Button>
                <Text type="secondary">|</Text>
                <Button
                  size="small"
                  type="link"
                  onClick={() => updateAllFieldPreferences(false)}
                  disabled={updating === "all_fields"}
                  loading={updating === "all_fields"}
                >
                  {t("emailPreferences.deselectAll")}
                </Button>
              </Space>
            )}
          </div>
        </Divider>

        {/* Field-based preferences */}
        {fieldPreferences.map((preference) => {
          const isUpdating = updating === preference.notification_type;
          const labelMap: Record<string, string> = {
            expected_delivery_date_change: t(
              "emailPreferences.expectedDeliveryDateChanges"
            ),
            container_operational_eta_change: t(
              "emailPreferences.containerOperationalEtaChanges"
            ),
            container_etd_change: t("emailPreferences.containerEtdChanges"),
          };

          return (
            <div
              key={preference.notification_type}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 0",
                borderBottom: "1px solid #f0f0f0",
                opacity: !globalEnabled ? 0.5 : 1,
              }}
            >
              <div>
                <Text strong={!globalEnabled}>
                  {labelMap[preference.notification_type] ||
                    preference.notification_type}
                </Text>
              </div>
              <Switch
                checked={globalEnabled && preference.enabled}
                onChange={(checked) =>
                  updateFieldPreference(preference.notification_type, checked)
                }
                disabled={!globalEnabled || isUpdating}
                loading={isUpdating}
              />
            </div>
          );
        })}

        <Divider orientation="left" style={{ margin: "16px 0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong>{t("emailPreferences.statusUpdates")}</Text>
            {globalEnabled &&
              preferences.filter(
                (p) =>
                  !n8nHandledStatuses.includes(p.status) &&
                  !excludedStatuses.includes(p.status)
              ).length > 0 && (
                <Space size="small">
                  <Button
                    size="small"
                    type="link"
                    onClick={() => updateAllStatusPreferences(true)}
                    disabled={updating === "all_statuses"}
                    loading={updating === "all_statuses"}
                  >
                    {t("emailPreferences.selectAll")}
                  </Button>
                  <Text type="secondary">|</Text>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => updateAllStatusPreferences(false)}
                    disabled={updating === "all_statuses"}
                    loading={updating === "all_statuses"}
                  >
                    {t("emailPreferences.deselectAll")}
                  </Button>
                </Space>
              )}
          </div>
        </Divider>

        {/* Status-based preferences */}
        {preferences
          .filter((preference) => !excludedStatuses.includes(preference.status)) // Exclude concept and archived
          .map((preference) => {
            const isN8nHandled = n8nHandledStatuses.includes(preference.status);
            const isDefaultEnabled = ["fyco", "delivery_planned"].includes(
              preference.status
            );
            const isUpdating = updating === preference.status;

            return (
              <div
                key={preference.status}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 0",
                  borderBottom: "1px solid #f0f0f0",
                  opacity: isN8nHandled ? 0.6 : 1,
                }}
              >
                <div>
                  <Text strong={isN8nHandled}>
                    {t(`status.${preference.status}`)}
                  </Text>
                  {isN8nHandled && (
                    <div>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        {t("emailPreferences.alwaysSent")}
                      </Text>
                    </div>
                  )}
                  {isDefaultEnabled && !isN8nHandled && (
                    <div>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        {t("emailPreferences.enabledByDefault")}
                      </Text>
                    </div>
                  )}
                </div>
                <Switch
                  checked={
                    // For default-enabled statuses: bypass global toggle, show actual preference state
                    // For others: require global toggle AND preference
                    isDefaultEnabled
                      ? preference.enabled
                      : globalEnabled && preference.enabled
                  }
                  onChange={(checked) =>
                    updatePreference(preference.status, checked)
                  }
                  disabled={!globalEnabled || isN8nHandled || isUpdating}
                  loading={isUpdating}
                />
              </div>
            );
          })}
      </Space>

      <div
        style={{
          marginTop: 16,
          padding: 12,
          backgroundColor: "#f6f8fa",
          borderRadius: 6,
        }}
      >
        <Text type="secondary" style={{ fontSize: "12px" }}>
          <strong>Note:</strong> {t("emailPreferences.note")}
        </Text>
      </div>
    </Card>
  );
};
